import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { get, withQuery, type ApiError } from "../api/client";
import type { QuotationList } from "../api/quotations";
import { useDebounced } from "../hooks/useDebounced";

interface SiteHit {
  id: number;
  name: string;
  address: string | null;
  rs_no: string | null;
  /** The works order: the City's notification number. */
  notif_no: string | null;
}

interface Hit {
  key: string;
  to: string;
  title: string;
  detail: string;
  kind: "Site" | "Quote";
}

const SHOWN = 5;

/** Find a site, a works order or a quotation from anywhere. Two searches the
 * server already answers -- the site picker's and the quotation register's --
 * side by side, once the typing pauses. Enter on nothing highlighted takes the
 * text to the Sites register, which searches everything. */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const settled = useDebounced(text.trim(), 250);
  const enabled = settled.length >= 2;

  const sites = useQuery<{ rows: SiteHit[] }, ApiError>({
    queryKey: ["global-search", "sites", settled],
    queryFn: () => get(withQuery("/api/v1/sites/search", { q: settled })),
    enabled,
    staleTime: 30_000,
  });
  const quotes = useQuery<QuotationList, ApiError>({
    queryKey: ["global-search", "quotes", settled],
    queryFn: () => get(withQuery("/api/v1/quotations", { search: settled })),
    enabled,
    staleTime: 30_000,
  });

  const hits: Hit[] = enabled
    ? [
        ...(sites.data?.rows ?? []).slice(0, SHOWN).map((site) => ({
          key: `s${site.id}`,
          to: `/sites/${site.id}`,
          title: site.name,
          detail: [site.notif_no, site.rs_no, site.address].filter(Boolean).join(" · "),
          kind: "Site" as const,
        })),
        ...(quotes.data?.rows ?? []).slice(0, SHOWN).map((quote) => ({
          key: `q${quote.id}`,
          to: `/commercial/${quote.id}`,
          title: quote.number,
          detail: [quote.site?.name, quote.supplier?.name].filter(Boolean).join(" · "),
          kind: "Quote" as const,
        })),
      ]
    : [];

  useEffect(() => {
    setActive(-1);
  }, [settled]);

  useEffect(() => {
    function away(event: MouseEvent) {
      if (box.current !== null && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  function go(to: string) {
    setOpen(false);
    setText("");
    navigate(to);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, hits.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, -1));
    } else if (event.key === "Escape") {
      setOpen(false);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = hits[active];
      if (hit !== undefined) {
        go(hit.to);
      } else if (text.trim() !== "") {
        go(`/sites?q=${encodeURIComponent(text.trim())}`);
      }
    }
  }

  const loading = enabled && (sites.isFetching || quotes.isFetching);
  const showList = open && text.trim().length >= 2;

  return (
    <div className="global-search" ref={box}>
      <span className="global-search-icon" aria-hidden="true">
        ⌕
      </span>
      <input
        type="search"
        role="combobox"
        aria-label="Find a site, works order or quote"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        placeholder="Find a site, works order or quote"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showList ? (
        <ul className="global-search-list" id={listId} role="listbox">
          {hits.map((hit, index) => (
            <li
              key={hit.key}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? "active" : undefined}
              onMouseDown={(event) => {
                event.preventDefault();
                go(hit.to);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="global-search-kind">{hit.kind}</span>
              <span className="global-search-title">{hit.title}</span>
              {hit.detail === "" ? null : <span className="global-search-detail">{hit.detail}</span>}
            </li>
          ))}
          {hits.length === 0 ? (
            <li className="global-search-none" role="presentation">
              {loading || settled !== text.trim() ? "Searching…" : "Nothing matches. Enter searches Sites."}
            </li>
          ) : (
            <li className="global-search-none" role="presentation">
              Enter searches every site
            </li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
